<template>
  <div class="deposit-modal _modal">
    <div class="_modal-overlay" @click.self="closeDepositModal"></div>
    <div class="deposit-modal__inner _modal-inner">
      <div class="_modal-close" @click="closeDepositModal"></div>
      <div class="deposit-modal__content">
        <div v-if="!payout" class="deposit-modal__title _md">
          {{ $t('Deposit') }}
        </div>
        <div v-if="payout" class="deposit-modal__title _md">
          {{ $t('Payout') }}
        </div>
        <div class="deposit-modal__inputs">
          <BaseInput
            v-model="name"
            type="text"
            :label="`${$t('First name')}:`"
            class="deposit-modal__input"
          />
          <BaseInput
            v-model="surname"
            type="text"
            :label="`${$t('Last Name')}:`"
            class="deposit-modal__input"
          />
          <BaseInput
            v-model="email"
            type="email"
            :label="`${$t('Email')}:`"
            class="deposit-modal__input"
          />
          <BaseInput
            class="deposit-modal__input"
            v-model="phone"
            type="text"
            :label="`${$t('Phone number')}:`"
          />
          <div class="deposit-modal__input">
            <div class="deposit-modal__select-label _select-label">
              {{ `${$t('Country')}:` }}
            </div>
            <select class="deposit-modal__select _select" v-model="countryId">
              <option
                v-for="option in countryOptions"
                :key="option.id"
                :value="option.id"
              >
                {{ option.title }}
              </option>
            </select>
          </div>
          <BaseInput
            v-model="city"
            type="text"
            :label="`${$t('City')}:`"
            class="deposit-modal__input"
          />
          <BaseInput
            v-model="address"
            type="text"
            :label="`${$t('Address')}:`"
            class="deposit-modal__input"
          />
          <BaseInput
            v-model="zip"
            type="text"
            :label="`${$t('Post Code')}:`"
            class="deposit-modal__input"
          />
          <BaseTermCheckbox v-model="terms" class="deposit-modal__checkbox">
            {{ $t('I’ve read and agree to the') }}
            <router-link
              v-if="termsPage && termsPage.slug"
              :to="{ name: 'TextPage', params: { slug: termsPage.slug } }"
              class="desc small"
              target="_blank"
            >
              {{ termsPage.title }}
            </router-link>
            <template v-if="termsPage && privacyPage">
              <span class="desc small">
                {{ ',' + ' ' }}
              </span>
              <router-link
                v-if="privacyPage && privacyPage.slug"
                :to="{ name: 'TextPage', params: { slug: privacyPage.slug } }"
                class="desc small"
                target="_blank"
              >
                {{ privacyPage.title }}
              </router-link>
              <span class="desc small">
                {{ $t(', and confirm I’m 18 or older.') }}
              </span>
            </template>
          </BaseTermCheckbox>
        </div>
        <div class="deposit-modal__bottom">
          <div class="deposit-modal__price">
            <div class="deposit-modal__price-value _h3">
              {{ depositAmount }}
            </div>
            <div class="deposit-modal__price-symbol _h3">
              {{ currencySymbol || currencyCode }}
            </div>
          </div>
          <Transition>
            <div v-if="error" class="deposit-modal__error">
              {{ error }}
            </div>
          </Transition>
          <div
            v-if="paymentMethods && paymentMethods.length"
            class="deposit-modal__methods"
          >
            <BaseButton
              v-for="method in paymentMethods"
              :key="method.code"
              class="deposit-modal__submit _btn-primary"
              :loading="isSubmitting && selectedPaymentType === method.code"
              :disabled="!requiredFieldsAreFilled || isSubmitting"
              @click="submitDeposit(method.code)"
            >
              <span v-if="isSubmitting && selectedPaymentType === method.code">
                {{ $t('Submiting') }}...
              </span>
              <span v-else>
                {{ method.title }}
              </span>
            </BaseButton>
          </div>
          <div v-else>
            <BaseButton
              class="deposit-modal__submit _btn-primary"
              :loading="isSubmitting"
              :disabled="!requiredFieldsAreFilled || isSubmitting"
              @click="submitDeposit()"
            >
              <span v-if="isSubmitting">{{ $t('Submiting') }}...</span>
              <span v-else>{{ $t('CREDIT/DEBIT CARD') }}</span>
            </BaseButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { PRIVACY_PAGE, TERMS_PAGE } from '@/store/modules/staticPages'
import { mapActions, mapGetters } from 'vuex'
import BaseButton, { ButtonThemes } from './base/BaseButton.vue'
import BaseInput from './base/BaseInput.vue'
import BaseTermCheckbox from './base/BaseTermCheckbox.vue'

export default {
  name: 'DepositModal',
  components: { BaseInput, BaseTermCheckbox, BaseButton },
  props: {
    user: Object,
    loaderIsVisible: {
      type: Boolean,
      required: false,
    },
    depositAmount: {
      type: Number,
      required: true,
    },
    payout: {
      type: Boolean,
      default: false,
    },
    depositCurrency: {
      type: String,
      default: '€',
    },
    paymentType: {
      type: String,
      required: true,
    },
    paymentMethods: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      ButtonThemes,
      isSubmitting: false,
      name: '',
      surname: '',
      email: '',
      phone: '',
      countryCode: '',
      phoneFull: '',
      countryOptions: [],
      countryOptionsIso: [],
      address: '',
      city: '',
      zip: '',
      countryId: '',
      error: '',
      saveData: false,
      terms: false,
      selectedPaymentType: this.paymentType || 'card',
    }
  },
  computed: {
    ...mapGetters('user', ['userData']),
    ...mapGetters('app', ['currencySymbol', 'currencyCode']),
    ...mapGetters('staticPages', ['getTermsPage', 'getPrivacyPage']),
    termsPage() {
      return this.getTermsPage
    },
    privacyPage() {
      return this.getPrivacyPage
    },
    requiredFieldsAreFilled() {
      return (
        this.name &&
        this.surname &&
        this.phone &&
        this.email &&
        this.countryId &&
        this.city &&
        this.address &&
        this.zip &&
        this.terms
      )
    },
  },
  created() {
    this.TERMS_PAGE = TERMS_PAGE
    this.PRIVACY_PAGE = PRIVACY_PAGE
  },
  mounted() {
    console.log('currencyCode:', this.currencyCode)
    console.log('currencySymbol:', this.currencySymbol)
    console.log('paymentMethods', this.paymentMethods)
    this.getCountries()
    this.initializeUserData()
    this.isSubmitting = false
  },
  methods: {
    ...mapActions('modals', ['closeDepositModal']),
    ...mapActions('user', ['fetchUserData']),
    initializeUserData() {
      console.log(this.userData)

      if (this.userData) {
        this.email = this.userData.email
        this.name = this.userData.name
        this.surname = this.userData.surname
        this.phone = this.userData.phone
        // this.countryCode = this.userData.country;
        this.address = this.userData.address
        this.city = this.userData.city
        this.zip = this.userData.zip
        this.countryId = this.userData.country
      }
    },
    phoneDataUpdate(e) {
      this.phoneFull = e.phoneNumber
    },
    async getCountries() {
      try {
        const response = await this.$http.get(`countries`)
        this.countryOptions = response.data.payload
        this.countryOptionsIso = response.data.payload.map((obj) => obj.iso)
        this.countryCode = this.countryOptionsIso[0]
      } catch (error) {
        console.error('Failed to fetch countries:', error)
      }
    },
    async submitDeposit(methodCode) {
      if (!this.requiredFieldsAreFilled) return
      this.isSubmitting = true

      this.selectedPaymentType =
        methodCode || this.selectedPaymentType || 'card'

      const depositData = {
        name: this.name,
        surname: this.surname,
        email: this.email,
        phone: this.phone,
        country: this.countryId,
        city: this.city,
        address: this.address,
        postCode: this.zip,
        amount: this.depositAmount,
        paymentType: this.selectedPaymentType,
        currency: this.currencyCode,
        saveData: this.saveData,
      }

      try {
        const response = await this.$http.post(`deposit/checkout`, depositData)

        this.fetchUserData()

        if (response.data.status === 'ERROR') {
          this.$toast.error(this.$t(response.data.message))
          this.$router.push({ name: 'PaymentFailPage' })
          this.isSubmitting = false
          //this.closeDepositModal();
        } else {
          window.location = response.data.redirect_url
          this.isSubmitting = false
          //this.closeDepositModal();
        }
      } catch (error) {
        console.log(error)
        this.$toast.error(this.$t(error.response.data.message))
        this.isSubmitting = false
        //this.closeDepositModal();
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/mixins' as *;
@use '@/assets/styles/media' as *;
@use '@/assets/styles/components/classes' as *;

.deposit-modal {
  &__inner {
    max-width: 506px;
  }
  &__content {
    max-width: 386px;
    margin: 0 auto;
  }
  &__title {
    text-transform: uppercase;
    text-align: center;
    &:not(:last-child) {
      @include adaptiveValue('margin-bottom', 25, 15);
    }
  }
  &__inputs {
    &:not(:last-child) {
      @include adaptiveValue('margin-bottom', 25, 15);
    }
  }
  &__input {
    &:not(:last-child) {
      @include adaptiveValue('margin-bottom', 15, 10);
    }
    &_phone {
    }
    &_select {
    }
  }
  &__checkbox {
    flex: 1 1 100%;
    a {
      color: var(--hint-color);
      transition: color 0.3s ease 0s;
      @media (any-hover: hover) {
        &:hover {
          color: var(--hint-hover-color);
        }
      }
    }
  }
  &__bottom {
    display: flex;
    gap: 20px;
    justify-content: space-between;
    align-items: center;
  }
  &__price {
    display: flex;
    gap: 5px;
  }
  &__price-symbol {
  }
  &__error {
    margin-bottom: 8px;
    text-align: center;
    color: var(--error-color);
  }
  &__submit {
    text-transform: uppercase;
    min-width: 160px;
    &:not(:last-child) {
      margin-bottom: 10px;
    }
  }
}
</style>
